import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Pagination,
    Alert,
    Snackbar,
    CircularProgress,
    Typography,
    Grid,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Divider,
    Card,
    CardContent,
    Stack,
    FormControlLabel,
    Checkbox,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Radio,
    RadioGroup,
    FormLabel
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SearchIcon from '@mui/icons-material/Search';
import {
    ExEmployee,
    ExEmployeesResponse,
    TerminatedEmployee,
    SearchTerminatedEmployeesResponse,
    SearchTerminatedEmployeesRequest,
    AddOrReactivateExEmployeeRequest,
    ExtendExEmployeeRequest,
    DeactivateExEmployeeRequest
} from './types';
import httpClient from '../../../../utils/httpClient';
import { useMsal } from '@azure/msal-react';

const ManageExEmployees: React.FC = () => {
    const { accounts } = useMsal();
    const userFullName = accounts[0]?.idTokenClaims?.name || '';

    // State for ex-employees list
    const [exEmployees, setExEmployees] = useState<ExEmployee[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [page, setPage] = useState<number>(1);
    const [pageLimit] = useState<number>(50);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [includeDeactivated, setIncludeDeactivated] = useState<boolean>(false);

    // Expanded rows for history accordion
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

    // Add/Search dialog state
    const [addDialogOpen, setAddDialogOpen] = useState<boolean>(false);
    const [searchLoading, setSearchLoading] = useState<boolean>(false);
    const [searchResults, setSearchResults] = useState<TerminatedEmployee[]>([]);
    const [selectedEmployee, setSelectedEmployee] = useState<TerminatedEmployee | null>(null);
    const [searchPage, setSearchPage] = useState<number>(1);
    const [searchTotalCount, setSearchTotalCount] = useState<number>(0);
    const [searchPerformed, setSearchPerformed] = useState<boolean>(false);
    const [searchForm, setSearchForm] = useState<SearchTerminatedEmployeesRequest>({
        page: 1,
        pageLimit: 50
    });
    const [addNotes, setAddNotes] = useState<string>('');
    const [addNameConfirmation, setAddNameConfirmation] = useState<string>('');

    // Extend dialog state
    const [extendDialogOpen, setExtendDialogOpen] = useState<boolean>(false);
    const [extendEmployee, setExtendEmployee] = useState<ExEmployee | null>(null);
    const [extendNotes, setExtendNotes] = useState<string>('');
    const [extendNameConfirmation, setExtendNameConfirmation] = useState<string>('');

    // Deactivate dialog state
    const [deactivateDialogOpen, setDeactivateDialogOpen] = useState<boolean>(false);
    const [deactivateEmployee, setDeactivateEmployee] = useState<ExEmployee | null>(null);
    const [deactivateNotes, setDeactivateNotes] = useState<string>('');
    const [deactivateNameConfirmation, setDeactivateNameConfirmation] = useState<string>('');

    // Alert state
    const [alert, setAlert] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info' | 'warning';
    }>({
        open: false,
        message: '',
        severity: 'info'
    });

    // Fetch ex-employees
    const fetchExEmployees = async () => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                page: page.toString(),
                pageLimit: pageLimit.toString(),
                'include-deactivated': includeDeactivated.toString()
            });

            const res = await httpClient.get<ExEmployeesResponse>(`/api/manage/ex-employees?${queryParams.toString()}`);
            
            setExEmployees(res.data.exEmployees);
            setTotalCount(res.data.totalCount);
        } catch (error: any) {
            showAlert('Failed to load ex-employees', 'error');
            console.error('Error fetching ex-employees:', error);
        } finally {
            setLoading(false);
        }
    };

    // Search terminated employees
    const searchTerminatedEmployees = async () => {
        setSearchLoading(true);
        try {
            const queryParams = new URLSearchParams({
                Page: searchPage.toString(),
                PageLimit: '50'
            });

            // Check if there are any search criteria
            const hasSearchCriteria = !!(
                searchForm.terminationDate ||
                searchForm.terminationDateFrom ||
                searchForm.terminationDateTo ||
                searchForm.employeeNumber ||
                searchForm.name ||
                searchForm.emailAddress
            );

            if (searchForm.terminationDate) queryParams.append('TerminationDate', searchForm.terminationDate);
            if (searchForm.terminationDateFrom) queryParams.append('TerminationDateFrom', searchForm.terminationDateFrom);
            if (searchForm.terminationDateTo) queryParams.append('TerminationDateTo', searchForm.terminationDateTo);
            if (searchForm.employeeNumber) queryParams.append('EmployeeNumber', searchForm.employeeNumber.toString());
            if (searchForm.name) queryParams.append('Name', searchForm.name);
            if (searchForm.emailAddress) queryParams.append('EmailAddress', searchForm.emailAddress);

            const res = await httpClient.get<SearchTerminatedEmployeesResponse>(`/api/manage/ex-employees/search?${queryParams.toString()}`);
            
            // Set results first, then set searchPerformed to avoid flash of "no results" message
            setSearchResults(res.data.employees || []);
            setSearchTotalCount(res.data.totalCount || 0);
            
            // Set searchPerformed after results are set
            setSearchPerformed(true);
            
            // Only show "no results" message if there were actual search criteria
            if (hasSearchCriteria && res.data.employees && res.data.employees.length === 0) {
                showAlert('No employees found matching the search criteria', 'info');
            }
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to search terminated employees';
            showAlert(errorMessage, 'error');
            setSearchResults([]);
            setSearchTotalCount(0);
            setSearchPerformed(true);
            console.error('Error searching terminated employees:', error);
        } finally {
            setSearchLoading(false);
        }
    };

    // Handle add ex-employee
    const handleAddExEmployee = async () => {
        if (!selectedEmployee) {
            showAlert('Please select an employee', 'warning');
            return;
        }

        if (!addNotes.trim() || addNotes.trim().length < 10) {
            showAlert('Notes must be at least 10 characters long', 'warning');
            return;
        }

        if (addNameConfirmation.trim() !== userFullName.trim()) {
            showAlert('Full name confirmation does not match', 'warning');
            return;
        }

        try {
            const request: AddOrReactivateExEmployeeRequest = {
                employeeNumber: selectedEmployee.employeeNumber,
                notes: addNotes.trim()
            };

            await httpClient.post('/api/manage/ex-employee', request);
            
            showAlert('Ex-employee added/reactivated successfully', 'success');
            handleCloseAddDialog();
            fetchExEmployees();
        } catch (error: any) {
            showAlert(error?.response?.data?.message || 'Failed to add ex-employee', 'error');
            console.error('Error adding ex-employee:', error);
        }
    };

    // Handle extend ex-employee
    const handleExtendExEmployee = async () => {
        if (!extendEmployee) return;

        if (!extendNotes.trim() || extendNotes.trim().length < 10) {
            showAlert('Notes must be at least 10 characters long', 'warning');
            return;
        }

        if (extendNameConfirmation.trim() !== userFullName.trim()) {
            showAlert('Full name confirmation does not match', 'warning');
            return;
        }

        try {
            const request: ExtendExEmployeeRequest = {
                notes: extendNotes.trim()
            };

            await httpClient.put(`/api/manage/ex-employee/${extendEmployee.exEmployeeId}/extend`, request);
            
            showAlert('Ex-employee validity extended successfully', 'success');
            handleCloseExtendDialog();
            fetchExEmployees();
        } catch (error: any) {
            showAlert(error?.response?.data?.message || 'Failed to extend ex-employee', 'error');
            console.error('Error extending ex-employee:', error);
        }
    };

    // Handle deactivate ex-employee
    const handleDeactivateExEmployee = async () => {
        if (!deactivateEmployee) return;

        if (!deactivateNotes.trim() || deactivateNotes.trim().length < 10) {
            showAlert('Notes must be at least 10 characters long', 'warning');
            return;
        }

        if (deactivateNameConfirmation.trim() !== userFullName.trim()) {
            showAlert('Full name confirmation does not match', 'warning');
            return;
        }

        try {
            const request: DeactivateExEmployeeRequest = {
                notes: deactivateNotes.trim()
            };

            await httpClient.put(`/api/manage/ex-employee/${deactivateEmployee.exEmployeeId}/deactivate`, request);
            
            showAlert('Ex-employee deactivated successfully', 'success');
            handleCloseDeactivateDialog();
            fetchExEmployees();
        } catch (error: any) {
            showAlert(error?.response?.data?.message || 'Failed to deactivate ex-employee', 'error');
            console.error('Error deactivating ex-employee:', error);
        }
    };

    // Handle toggle history accordion
    const handleToggleHistory = (exEmployeeId: number) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(exEmployeeId)) {
            newExpanded.delete(exEmployeeId);
        } else {
            newExpanded.add(exEmployeeId);
        }
        setExpandedRows(newExpanded);
    };

    // Handle open add dialog
    const handleOpenAddDialog = () => {
        setAddDialogOpen(true);
        setSearchForm({ page: 1, pageLimit: 50 });
        setSearchResults([]);
        setSelectedEmployee(null);
        setAddNotes('');
        setAddNameConfirmation('');
        setSearchPage(1);
        setSearchPerformed(false);
    };

    // Handle close add dialog
    const handleCloseAddDialog = () => {
        setAddDialogOpen(false);
        setSearchForm({ page: 1, pageLimit: 50 });
        setSearchResults([]);
        setSelectedEmployee(null);
        setAddNotes('');
        setAddNameConfirmation('');
        setSearchPage(1);
        setSearchPerformed(false);
    };

    // Handle open extend dialog
    const handleOpenExtendDialog = (employee: ExEmployee) => {
        setExtendEmployee(employee);
        setExtendNotes('');
        setExtendNameConfirmation('');
        setExtendDialogOpen(true);
    };

    // Handle close extend dialog
    const handleCloseExtendDialog = () => {
        setExtendDialogOpen(false);
        setExtendEmployee(null);
        setExtendNotes('');
        setExtendNameConfirmation('');
    };

    // Handle open deactivate dialog
    const handleOpenDeactivateDialog = (employee: ExEmployee) => {
        setDeactivateEmployee(employee);
        setDeactivateNotes('');
        setDeactivateNameConfirmation('');
        setDeactivateDialogOpen(true);
    };

    // Handle close deactivate dialog
    const handleCloseDeactivateDialog = () => {
        setDeactivateDialogOpen(false);
        setDeactivateEmployee(null);
        setDeactivateNotes('');
        setDeactivateNameConfirmation('');
    };

    // Show alert
    const showAlert = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
        setAlert({ open: true, message, severity });
    };

    // Format date
    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    // Check if expired
    const isExpired = (validTill: string) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const validDate = new Date(validTill);
        validDate.setHours(0, 0, 0, 0);
        return validDate < today;
    };

    useEffect(() => {
        fetchExEmployees();
    }, [page, includeDeactivated]);

    const totalPages = Math.ceil(totalCount / pageLimit);
    const searchTotalPages = Math.ceil(searchTotalCount / 50);

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Manage Ex-Employees
            </Typography>

            {/* Controls */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={includeDeactivated}
                            onChange={(e) => {
                                setIncludeDeactivated(e.target.checked);
                                setPage(1);
                            }}
                        />
                    }
                    label="Show Expired Records (ValidTill < Today)"
                />
                <Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenAddDialog}
                        sx={{ mr: 1 }}
                    >
                        Add Ex-Employee
                    </Button>
                    <IconButton onClick={fetchExEmployees} disabled={loading}>
                        <RefreshIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Full Name</TableCell>
                            <TableCell>Email Address</TableCell>
                            <TableCell>Employee Number</TableCell>
                            <TableCell>Added On</TableCell>
                            <TableCell>Updated On</TableCell>
                            <TableCell>Valid Till</TableCell>
                            <TableCell>History</TableCell>
                            <TableCell>Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : exEmployees.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    No ex-employees found
                                </TableCell>
                            </TableRow>
                        ) : (
                            exEmployees.map((employee) => (
                                <React.Fragment key={employee.exEmployeeId}>
                                    <TableRow>
                                        <TableCell>{employee.fullName}</TableCell>
                                        <TableCell>{employee.emailAddress}</TableCell>
                                        <TableCell>{employee.employeeNumber}</TableCell>
                                        <TableCell>{formatDate(employee.addedOn)}</TableCell>
                                        <TableCell>{formatDate(employee.updatedOn)}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {formatDate(employee.validTill)}
                                                {isExpired(employee.validTill) && (
                                                    <Chip label="Expired" color="error" size="small" />
                                                )}
                                                {!isExpired(employee.validTill) && (
                                                    <Chip label="Active" color="success" size="small" />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleToggleHistory(employee.exEmployeeId)}
                                            >
                                                <HistoryIcon />
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={() => handleOpenExtendDialog(employee)}
                                                    disabled={isExpired(employee.validTill)}
                                                >
                                                    Extend
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={() => handleOpenDeactivateDialog(employee)}
                                                    disabled={isExpired(employee.validTill)}
                                                >
                                                    Remove
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    {expandedRows.has(employee.exEmployeeId) && employee.history && (
                                        <TableRow>
                                            <TableCell colSpan={8} sx={{ py: 0, backgroundColor: (theme) => theme.palette.grey[200] }}>
                                                <Accordion expanded={true} sx={{ backgroundColor: (theme) => theme.palette.grey[100], boxShadow: 'none' }}>
                                                    <AccordionSummary>
                                                        <Typography variant="subtitle2" sx={{ color: (theme) => theme.palette.grey[700] }}>History</Typography>
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                        <Stack spacing={2}>
                                                            {employee.history.map((history) => (
                                                                <Card key={history.exEmployeeHistoryId} variant="outlined" sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}>
                                                                    <CardContent>
                                                                        <Grid container spacing={2}>
                                                                            <Grid item xs={12} sm={6}>
                                                                                <Typography variant="caption" color="textSecondary">
                                                                                    Action
                                                                                </Typography>
                                                                                <Box sx={{ color: (theme) => theme.palette.grey[700] }}>
                                                                                    <Chip
                                                                                        label={history.action}
                                                                                        size="small"
                                                                                        color={
                                                                                            history.action === 'Added' || history.action === 'Re-added'
                                                                                                ? 'success'
                                                                                                : history.action === 'Extended'
                                                                                                ? 'info'
                                                                                                : 'error'
                                                                                        }
                                                                                    />
                                                                                </Box>
                                                                            </Grid>
                                                                            <Grid item xs={12} sm={6}>
                                                                                <Typography variant="caption" color="textSecondary">
                                                                                    Date
                                                                                </Typography>
                                                                                <Typography variant="body2" sx={{ color: (theme) => theme.palette.grey[700] }}>
                                                                                    {formatDate(history.createdOn)}
                                                                                </Typography>
                                                                            </Grid>
                                                                            <Grid item xs={12} sm={6}>
                                                                                <Typography variant="caption" color="textSecondary">
                                                                                    Previous Valid Till
                                                                                </Typography>
                                                                                <Typography variant="body2" sx={{ color: (theme) => theme.palette.grey[700] }}>
                                                                                    {history.previousValidTill ? formatDate(history.previousValidTill) : 'N/A'}
                                                                                </Typography>
                                                                            </Grid>
                                                                            <Grid item xs={12} sm={6}>
                                                                                <Typography variant="caption" color="textSecondary">
                                                                                    New Valid Till
                                                                                </Typography>
                                                                                <Typography variant="body2" sx={{ color: (theme) => theme.palette.grey[700] }}>
                                                                                    {formatDate(history.newValidTill)}
                                                                                </Typography>
                                                                            </Grid>
                                                                            <Grid item xs={12}>
                                                                                <Typography variant="caption" color="textSecondary">
                                                                                    Notes
                                                                                </Typography>
                                                                                <Typography variant="body2" sx={{ color: (theme) => theme.palette.grey[700] }}>
                                                                                    {history.notes}
                                                                                </Typography>
                                                                            </Grid>
                                                                            <Grid item xs={12} sm={6}>
                                                                                <Typography variant="caption" color="textSecondary">
                                                                                    Updated By
                                                                                </Typography>
                                                                                <Typography variant="body2" sx={{ color: (theme) => theme.palette.grey[700] }}>
                                                                                    {history.updatedBy}
                                                                                </Typography>
                                                                            </Grid>
                                                                        </Grid>
                                                                    </CardContent>
                                                                </Card>
                                                            ))}
                                                        </Stack>
                                                    </AccordionDetails>
                                                </Accordion>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, value) => setPage(value)}
                        color="primary"
                    />
                </Box>
            )}

            {/* Add/Search Dialog */}
            <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">Add Ex-Employee</Typography>
                        <IconButton onClick={handleCloseAddDialog}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={3}>
                        {/* Search Form */}
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Search Terminated Employees
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <FormLabel>Termination Date Filter</FormLabel>
                                            <RadioGroup
                                                row
                                                value={searchForm.terminationDate !== undefined ? 'exact' : searchForm.terminationDateFrom !== undefined ? 'range' : 'none'}
                                                onChange={(e) => {
                                                    if (e.target.value === 'none') {
                                                        setSearchForm({ ...searchForm, terminationDate: undefined, terminationDateFrom: undefined, terminationDateTo: undefined });
                                                    } else if (e.target.value === 'exact') {
                                                        setSearchForm({ ...searchForm, terminationDate: searchForm.terminationDate || '', terminationDateFrom: undefined, terminationDateTo: undefined });
                                                    } else if (e.target.value === 'range') {
                                                        setSearchForm({ ...searchForm, terminationDate: undefined, terminationDateFrom: searchForm.terminationDateFrom || '', terminationDateTo: searchForm.terminationDateTo || '' });
                                                    }
                                                }}
                                            >
                                                <FormControlLabel value="none" control={<Radio />} label="None" />
                                                <FormControlLabel value="exact" control={<Radio />} label="Exact Date" />
                                                <FormControlLabel value="range" control={<Radio />} label="Date Range" />
                                            </RadioGroup>
                                        </FormControl>
                                    </Grid>
                                    {searchForm.terminationDate !== undefined && (
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Termination Date"
                                                type="date"
                                                value={searchForm.terminationDate || ''}
                                                onChange={(e) => setSearchForm({ ...searchForm, terminationDate: e.target.value || undefined })}
                                                required
                                                error={searchForm.terminationDate !== undefined && !searchForm.terminationDate}
                                                helperText={searchForm.terminationDate !== undefined && !searchForm.terminationDate ? 'Termination Date is required' : ''}
                                                InputLabelProps={{
                                                    shrink: true,
                                                    sx: {
                                                        backgroundColor: 'background.paper',
                                                        px: 0.5
                                                    }
                                                }}
                                            />
                                        </Grid>
                                    )}
                                    {searchForm.terminationDateFrom !== undefined && (
                                        <>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Termination Date From"
                                                    type="date"
                                                    value={searchForm.terminationDateFrom || ''}
                                                    onChange={(e) => setSearchForm({ ...searchForm, terminationDateFrom: e.target.value || undefined })}
                                                    required
                                                    error={searchForm.terminationDateFrom !== undefined && !searchForm.terminationDateFrom}
                                                    helperText={searchForm.terminationDateFrom !== undefined && !searchForm.terminationDateFrom ? 'Termination Date From is required' : ''}
                                                    InputLabelProps={{
                                                        shrink: true,
                                                        sx: {
                                                            backgroundColor: 'background.paper',
                                                            px: 0.5
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Termination Date To"
                                                    type="date"
                                                    value={searchForm.terminationDateTo || ''}
                                                    onChange={(e) => setSearchForm({ ...searchForm, terminationDateTo: e.target.value || undefined })}
                                                    required
                                                    error={searchForm.terminationDateTo !== undefined && !searchForm.terminationDateTo}
                                                    helperText={searchForm.terminationDateTo !== undefined && !searchForm.terminationDateTo ? 'Termination Date To is required' : ''}
                                                    InputLabelProps={{
                                                        shrink: true,
                                                        sx: {
                                                            backgroundColor: 'background.paper',
                                                            px: 0.5
                                                        }
                                                    }}
                                                />
                                            </Grid>
                                        </>
                                    )}
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Employee Number"
                                            type="number"
                                            inputMode="numeric"
                                            value={searchForm.employeeNumber || ''}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                // Only allow numeric input
                                                if (value === '' || /^\d+$/.test(value)) {
                                                    setSearchForm({ ...searchForm, employeeNumber: value ? parseInt(value) : undefined });
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                // Prevent non-numeric keys except backspace, delete, tab, escape, enter, and arrow keys
                                                if (!/[0-9]/.test(e.key) && 
                                                    !['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key) &&
                                                    !(e.ctrlKey || e.metaKey)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                            InputLabelProps={{
                                                sx: {
                                                    backgroundColor: 'background.paper',
                                                    px: 0.5
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Name (partial match)"
                                            value={searchForm.name || ''}
                                            onChange={(e) => setSearchForm({ ...searchForm, name: e.target.value || undefined })}
                                            InputLabelProps={{
                                                sx: {
                                                    backgroundColor: 'background.paper',
                                                    px: 0.5
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Email Address (partial match)"
                                            value={searchForm.emailAddress || ''}
                                            onChange={(e) => setSearchForm({ ...searchForm, emailAddress: e.target.value || undefined })}
                                            InputLabelProps={{
                                                sx: {
                                                    backgroundColor: 'background.paper',
                                                    px: 0.5
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            variant="contained"
                                            startIcon={<SearchIcon />}
                                            onClick={() => {
                                                setSearchPage(1);
                                                searchTerminatedEmployees();
                                            }}
                                            disabled={
                                                searchLoading ||
                                                (searchForm.terminationDate !== undefined && !searchForm.terminationDate) ||
                                                (searchForm.terminationDateFrom !== undefined && (!searchForm.terminationDateFrom || !searchForm.terminationDateTo))
                                            }
                                        >
                                            Search
                                        </Button>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Search Results */}
                        {searchPerformed && !searchLoading && (
                            <Card variant="outlined">
                                <CardContent>
                                    {searchResults.length > 0 ? (
                                        <>
                                            <Typography variant="h6" gutterBottom>
                                                Search Results ({searchTotalCount} found)
                                            </Typography>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell padding="checkbox" />
                                                    <TableCell>Employee Number</TableCell>
                                                    <TableCell>Full Name</TableCell>
                                                    <TableCell>Email Address</TableCell>
                                                    <TableCell>Termination Date</TableCell>
                                                    <TableCell>Status</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {searchResults.map((emp) => (
                                                    <TableRow key={emp.employeeNumber}>
                                                        <TableCell padding="checkbox">
                                                            <Checkbox
                                                                checked={selectedEmployee?.employeeNumber === emp.employeeNumber}
                                                                onChange={() => setSelectedEmployee(emp)}
                                                            />
                                                        </TableCell>
                                                        <TableCell>{emp.employeeNumber}</TableCell>
                                                        <TableCell>{emp.employeeFullName}</TableCell>
                                                        <TableCell>{emp.emailAddress}</TableCell>
                                                        <TableCell>{emp.terminationDate ? formatDate(emp.terminationDate) : 'N/A'}</TableCell>
                                                        <TableCell>
                                                            {emp.isAlreadyActive ? (
                                                                <Chip label="Already Active" color="warning" size="small" />
                                                            ) : emp.canReactivate ? (
                                                                <Chip label="Can Reactivate" color="info" size="small" />
                                                            ) : (
                                                                <Chip label="Available" color="success" size="small" />
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    {searchTotalPages > 1 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                            <Pagination
                                                count={searchTotalPages}
                                                page={searchPage}
                                                onChange={(_, value) => {
                                                    setSearchPage(value);
                                                    searchTerminatedEmployees();
                                                }}
                                                color="primary"
                                            />
                                        </Box>
                                    )}
                                        </>
                                    ) : (() => {
                                        // Only show "no results" if there were actual search criteria
                                        const hasSearchCriteria = !!(
                                            searchForm.terminationDate ||
                                            searchForm.terminationDateFrom ||
                                            searchForm.terminationDateTo ||
                                            searchForm.employeeNumber ||
                                            searchForm.name ||
                                            searchForm.emailAddress
                                        );
                                        return hasSearchCriteria ? (
                                            <Typography variant="body1" align="center" sx={{ py: 3, color: (theme) => theme.palette.error.light }}>
                                                No employees found matching the search criteria.
                                            </Typography>
                                        ) : null;
                                    })()}
                                </CardContent>
                            </Card>
                        )}

                        {/* Confirmation Form */}
                        {selectedEmployee && (
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Confirmation
                                    </Typography>
                                    <Stack spacing={2}>
                                        <TextField
                                            fullWidth
                                            label="Notes (required, min 10 characters)"
                                            multiline
                                            rows={4}
                                            value={addNotes}
                                            onChange={(e) => setAddNotes(e.target.value)}
                                            error={addNotes.trim().length > 0 && addNotes.trim().length < 10}
                                            helperText={addNotes.trim().length > 0 && addNotes.trim().length < 10 ? 'Notes must be at least 10 characters' : ''}
                                            required
                                            InputLabelProps={{
                                                sx: {
                                                    backgroundColor: 'background.paper',
                                                    px: 0.5
                                                }
                                            }}
                                        />
                                        <TextField
                                            fullWidth
                                            label={`Full Name Confirmation (must match: ${userFullName})`}
                                            value={addNameConfirmation}
                                            onChange={(e) => setAddNameConfirmation(e.target.value)}
                                            error={addNameConfirmation.trim() !== '' && addNameConfirmation.trim() !== userFullName.trim()}
                                            helperText={addNameConfirmation.trim() !== '' && addNameConfirmation.trim() !== userFullName.trim() ? 'Full name does not match' : ''}
                                            required
                                            InputLabelProps={{
                                                sx: {
                                                    backgroundColor: 'background.paper',
                                                    px: 0.5
                                                }
                                            }}
                                        />
                                    </Stack>
                                </CardContent>
                            </Card>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseAddDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleAddExEmployee}
                        disabled={!selectedEmployee || !addNotes.trim() || addNotes.trim().length < 10 || addNameConfirmation.trim() !== userFullName.trim()}
                    >
                        Add
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Extend Dialog */}
            <Dialog open={extendDialogOpen} onClose={handleCloseExtendDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Extend Ex-Employee Validity</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {extendEmployee && (
                            <>
                                <Typography variant="body2" color="textSecondary">
                                    Employee: {extendEmployee.fullName} ({extendEmployee.employeeNumber})
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Current Valid Till: {formatDate(extendEmployee.validTill)}
                                </Typography>
                            </>
                        )}
                        <TextField
                            fullWidth
                            label="Notes (required, min 10 characters)"
                            multiline
                            rows={4}
                            value={extendNotes}
                            onChange={(e) => setExtendNotes(e.target.value)}
                            error={extendNotes.trim().length > 0 && extendNotes.trim().length < 10}
                            helperText={extendNotes.trim().length > 0 && extendNotes.trim().length < 10 ? 'Notes must be at least 10 characters' : ''}
                            required
                            InputLabelProps={{
                                sx: {
                                    backgroundColor: 'background.paper',
                                    px: 0.5
                                }
                            }}
                        />
                        <TextField
                            fullWidth
                            label={`Full Name Confirmation (must match: ${userFullName})`}
                            value={extendNameConfirmation}
                            onChange={(e) => setExtendNameConfirmation(e.target.value)}
                            error={extendNameConfirmation.trim() !== '' && extendNameConfirmation.trim() !== userFullName.trim()}
                            helperText={extendNameConfirmation.trim() !== '' && extendNameConfirmation.trim() !== userFullName.trim() ? 'Full name does not match' : ''}
                            required
                            InputLabelProps={{
                                sx: {
                                    backgroundColor: 'background.paper',
                                    px: 0.5
                                }
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseExtendDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleExtendExEmployee}
                        disabled={!extendNotes.trim() || extendNotes.trim().length < 10 || extendNameConfirmation.trim() !== userFullName.trim()}
                    >
                        Extend
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Deactivate Dialog */}
            <Dialog open={deactivateDialogOpen} onClose={handleCloseDeactivateDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Deactivate Ex-Employee</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {deactivateEmployee && (
                            <>
                                <Typography variant="body2" color="textSecondary">
                                    Employee: {deactivateEmployee.fullName} ({deactivateEmployee.employeeNumber})
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Current Valid Till: {formatDate(deactivateEmployee.validTill)}
                                </Typography>
                            </>
                        )}
                        <TextField
                            fullWidth
                            label="Notes (required, min 10 characters)"
                            multiline
                            rows={4}
                            value={deactivateNotes}
                            onChange={(e) => setDeactivateNotes(e.target.value)}
                            error={deactivateNotes.trim().length > 0 && deactivateNotes.trim().length < 10}
                            helperText={deactivateNotes.trim().length > 0 && deactivateNotes.trim().length < 10 ? 'Notes must be at least 10 characters' : ''}
                            required
                            InputLabelProps={{
                                sx: {
                                    backgroundColor: 'background.paper',
                                    px: 0.5
                                }
                            }}
                        />
                        <TextField
                            fullWidth
                            label={`Full Name Confirmation (must match: ${userFullName})`}
                            value={deactivateNameConfirmation}
                            onChange={(e) => setDeactivateNameConfirmation(e.target.value)}
                            error={deactivateNameConfirmation.trim() !== '' && deactivateNameConfirmation.trim() !== userFullName.trim()}
                            helperText={deactivateNameConfirmation.trim() !== '' && deactivateNameConfirmation.trim() !== userFullName.trim() ? 'Full name does not match' : ''}
                            required
                            InputLabelProps={{
                                sx: {
                                    backgroundColor: 'background.paper',
                                    px: 0.5
                                }
                            }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeactivateDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeactivateExEmployee}
                        disabled={!deactivateNotes.trim() || deactivateNotes.trim().length < 10 || deactivateNameConfirmation.trim() !== userFullName.trim()}
                    >
                        Deactivate
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Alert Snackbar */}
            <Snackbar
                open={alert.open}
                autoHideDuration={6000}
                onClose={() => setAlert({ ...alert, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={() => setAlert({ ...alert, open: false })} severity={alert.severity} sx={{ width: '100%' }}>
                    {alert.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ManageExEmployees;

